import React, { useEffect, useState } from 'react'
import { Paper, Chip, makeStyles } from '@material-ui/core'
import ActionBar from 'components/ActionBar'

import { useHistory, useLocation } from 'react-router-dom'
import ViewHeader from 'components/ViewHeader'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from '../../../components/ResourceNotFound'
import Loading from '../../../components/Loading'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  CheckCircleOutlineOutlined as SentIcon,
  ScheduleOutlined as ScheduleIcon
} from '@material-ui/icons'
import ConfirmModal from 'components/ConfirmationModal'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import getTableOptions from '../../../utils/getTableOptions'
import announcementSlice, {
  fetchAnnouncements,
  deleteAnnouncementById
} from '../../../store/announcements'
import { renderCol } from 'utils/renderColumns'
import { convertTimestampToLocalString } from 'utils/times'
import { AnnouncementStatus } from 'constants/index'
import RichTextRenderer from 'components/RichTextRenderer'

const StatusChip = ({ status }) => {
  const classes = useStyles()
  const isSent = status === AnnouncementStatus.SENT
  const color = isSent ? '#1B8A5A' : '#B26A00'
  const Icon = isSent ? SentIcon : ScheduleIcon
  return (
    <Chip
      size="small"
      icon={<Icon style={{ color, fontSize: 16 }} />}
      label={isSent ? 'Sent' : 'Scheduled'}
      className={isSent ? classes.sentChip : classes.scheduledChip}
    />
  )
}

const AnnouncementListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [deleteId, setDeleteId] = useState('')
  const [deleting, setDeleting] = useState(false)
  const { announcements, currentPage, pageSize, inProgress } = useSelector(
    (state) => state.announcements
  )

  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    dispatch(fetchAnnouncements())
  }, [dispatch])

  const handleConfirmDelete = async () => {
    setDeleting(true)
    await dispatch(deleteAnnouncementById(deleteId))
    setDeleting(false)
  }

  const closeDeleteModal = () => {
    setDeleteId('')
    setDeleting(false)
  }

  const tableHead = [
    'Announcement Text',
    {
      name: 'Status',
      options: {
        customBodyRender: (value) => <StatusChip status={value} />
      }
    },
    {
      name: 'Scheduled For',
      options: {
        customBodyRender: (value) =>
          value ? (
            <div style={{ minWidth: '130px' }}>
              {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
            </div>
          ) : (
            <span className={classes.muted}>—</span>
          )
      }
    },
    {
      name: 'Sent On',
      options: {
        customBodyRender: (value) =>
          value ? (
            <div style={{ minWidth: '130px' }}>
              {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
            </div>
          ) : (
            <span className={classes.muted}>—</span>
          )
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const formatTableData = (announcements) =>
    announcements.map((announcement) => {
      let sentOn
      if (announcement?.scheduledOn && announcement?.status === AnnouncementStatus.SENT) {
        sentOn = announcement?.scheduledOn
      } else if (announcement?.scheduledOn && announcement?.status !== AnnouncementStatus.SENT) {
        sentOn = ''
      } else {
        sentOn = announcement?.publishedOn
      }

      return [
        <div className={classes.textCell}>
          <RichTextRenderer htmlContent={announcement?.announcementText || ''} />
        </div>,
        announcement?.status,
        announcement?.scheduledOn,
        sentOn,
        <>
          <Tooltip title="Edit">
            <EditIcon
              color="action"
              style={{ cursor: 'pointer' }}
              onClick={() => history.push(`${location.pathname}/edit/${announcement.id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteIcon
              color="action"
              style={{ marginLeft: 5, cursor: 'pointer' }}
              onClick={() => setDeleteId(announcement.id)}
            />
          </Tooltip>
        </>
      ]
    })

  const options = getTableOptions()

  return (
    <>
      <ViewHeader title="Announcements">
        {inProgress && <Loading />}
        <ActionBar
          actionText="New Announcement"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Announcement"
        />
      </ViewHeader>

      {announcements && announcements.length ? (
        <Paper variant="outlined" className={classes.tableCard}>
          <BasicTable
            data={formatTableData(announcements)}
            columns={tableHead}
            options={{
              ...options,
              onChangePage: (page) => dispatch(announcementSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(announcementSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        !inProgress && <ResourceNotFound message="No announcement available" />
      )}

      <ConfirmModal
        dialogTitle="Delete Announcement"
        dialogText="Are you sure you want to delete this announcement?"
        open={!!deleteId}
        inProgress={deleting}
        closeModal={closeDeleteModal}
        handleConfirm={handleConfirmDelete}
      />
    </>
  )
}

export default AnnouncementListing

const useStyles = makeStyles(() => ({
  tableCard: {
    marginTop: 20,
    borderRadius: '12px',
    borderColor: '#E6E8EB',
    overflow: 'hidden'
  },
  textCell: {
    minWidth: '280px',
    maxWidth: '460px'
  },
  muted: {
    color: '#9aa0a6'
  },
  sentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#E6F7F0',
    color: '#1B8A5A',
    fontWeight: 600
  },
  scheduledChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#FEF3E0',
    color: '#B26A00',
    fontWeight: 600
  }
}))
